import { Bookmark } from "lucide-react";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "@tanstack/react-router";
import type { TFunction } from "i18next";
import { Badge } from "@/components/ui/badge";
import { FeedbackTray } from "@/components/Feedback/FeedbackTray";
import type { WorldSetting } from "@/types/summary";
import { CollapsibleCard } from "./CollapsibleCard";

function formatReadingTime(minutes: number, t: TFunction): string {
  if (minutes < 60)
    return t("bookDetails.readingTime.minutesOnly", { minutes });
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (remainingMinutes === 0)
    return t("bookDetails.readingTime.hoursOnly", { hours });
  return t("bookDetails.readingTime.hoursAndMinutes", {
    hours,
    minutes: remainingMinutes,
  });
}

interface BookDetailsCardProps {
  wordCount: number;
  readingTimeMinutes: number;
  genres?: string[];
  worldSetting?: WorldSetting;
}

const SECTION_LABEL_CLASSES =
  "text-[12px] font-medium leading-[100%] text-secondary-500";
const BADGE_CLASSES =
  "w-auto bg-slate-100 text-foreground rounded-full px-[10px] py-1 border-transparent text-[12px] leading-[16px] font-medium";

function DetailsSection({
  label,
  testId,
  children,
}: {
  label: string;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-[10px]" data-testid={testId}>
      <div className={SECTION_LABEL_CLASSES}>{label}</div>
      <div className="flex flex-wrap items-center gap-[10px]">{children}</div>
    </div>
  );
}

function WorldSettingBadges({
  worldSetting,
  primarySettings,
}: {
  worldSetting: WorldSetting;
  primarySettings: WorldSetting["settings"];
}) {
  return (
    <>
      {primarySettings.map((setting) => (
        <Fragment key={setting.location_label}>
          <Badge
            className={BADGE_CLASSES}
            data-testid="world-setting-badge-location"
          >
            {setting.location_label}
          </Badge>
          <Badge
            className={BADGE_CLASSES}
            data-testid="world-setting-badge-time-period"
          >
            {setting.time_period_label}
          </Badge>
        </Fragment>
      ))}
      <Badge
        className={BADGE_CLASSES}
        data-testid="world-setting-badge-social-structure"
      >
        {worldSetting.social_structure_label}
      </Badge>
      <Badge
        className={BADGE_CLASSES}
        data-testid="world-setting-badge-metaphysics"
      >
        {worldSetting.metaphysics_label}
      </Badge>
    </>
  );
}

export function BookDetailsCard({
  wordCount,
  readingTimeMinutes,
  genres,
  worldSetting,
}: BookDetailsCardProps) {
  const { t } = useTranslation("jobDetails");
  const { jobId } = useParams({ from: "/jobs/$jobId", strict: true });

  const hasGenres = genres && genres.length > 0;
  const primarySettings = worldSetting?.settings.filter(
    (s) => s.setting_type === "primary",
  );
  const hasWorldAndSettings = primarySettings && primarySettings.length > 0;
  const showExperimentalBadge = hasGenres || hasWorldAndSettings;

  return (
    <FeedbackTray
      cardTestId="book-details"
      jobId={jobId}
      contentType="book_details"
      promptKey="feedback.bookDetails.prompt"
    >
      <CollapsibleCard
        icon={Bookmark}
        title={t("bookDetails.title")}
        testId="book-details"
        showExperimentalBadge={showExperimentalBadge}
      >
        <div className="space-y-7 text-sm mt-5">
          {/* Information Section */}
          <DetailsSection
            label={t("bookDetails.information")}
            testId="information-section"
          >
            <Badge className={BADGE_CLASSES} data-testid="word-count-badge">
              {t("bookDetails.wordCount", {
                formattedCount: wordCount.toLocaleString(),
              })}
            </Badge>
            <Badge className={BADGE_CLASSES} data-testid="reading-time-badge">
              {formatReadingTime(readingTimeMinutes, t)}
            </Badge>
          </DetailsSection>

          {/* Genre Section */}
          {hasGenres && (
            <DetailsSection
              label={t("bookDetails.genre")}
              testId="genre-section"
            >
              {genres.map((genre) => (
                <Badge
                  key={genre}
                  className={BADGE_CLASSES}
                  data-testid="genre-badge"
                >
                  {genre}
                </Badge>
              ))}
            </DetailsSection>
          )}

          {/* World and Setting Section */}
          {hasWorldAndSettings && worldSetting && (
            <DetailsSection
              label={t("bookDetails.worldAndSetting")}
              testId="world-setting-section"
            >
              <WorldSettingBadges
                worldSetting={worldSetting}
                primarySettings={primarySettings}
              />
            </DetailsSection>
          )}
        </div>
      </CollapsibleCard>
    </FeedbackTray>
  );
}
